import React, {useCallback, useEffect, useState, useMemo} from 'react'
import {Route, Switch, useRouteMatch, useLocation} from 'react-router'
import {useMultiStyleConfig, VStack, Text, Flex} from '@chakra-ui/react'
import {Helmet} from 'react-helmet'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
// PARTIALS
import BannerLiveChat from './partials/banner-live-chat'
import SearchResults from './partials/search-results'
import FaqTitle from './partials/faq-title'
import GoBackButton from './partials/go-back-button'
// PAGES
import CategoriesList from './partials/categories-list'
import ArticlePage from './partials/article-page'
import ArticlesList from './partials/articles-list'
// COMPONENTS
import usePHPBKApi from '../../custom-api/hooks/use-phpkb-api'
import {FormattedMessage, useIntl} from 'react-intl'
import Breadcrumb from '../../components/breadcrumb'
import Search from '../../components/search'
import {FaqsBanner} from '../../components/editorial-components/faqs-banner'
import LoadingSpinner from '../../components/loading-spinner'
import EditorialComponentsFactory from '../../components/editorial-components/editorial-components-factory'
// HOOKS
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useFaqPageMeta} from '../../hooks/use-category-meta' // Import your new hook

//UTILS
import debounce from 'lodash/debounce'
import {useGetPageByType} from '../../custom-api/hooks/use-get-page-by-type'
import routingObject from '../../utils/static-routes'
import {remCalc} from '../../theme/_utils'
import CategoriesRouter from './partials/categories-router'
import {getSites} from '../../utils/site-utils'
import AlternateMetaTag from '../../components/metatag/alternate'
import loadable from '@loadable/component'
import {decodeHtmlEntities} from '../../utils/utils'

export default function FAQ() {
    const {res} = useServerContext()
    const SUGGESTIONS_LENGTH = 5
    const styles = useMultiStyleConfig('Faq', {})
    const {pageContainer} = styles || {}
    const {path} = useRouteMatch()
    const location = useLocation()
    const urlParams = new URLSearchParams(location?.search)
    const {usePHPBKFreeTextSearch, usePHPKBbyCountryAndLanguage, usePHPKBGetArticle} = usePHPBKApi()
    const [searchQuery, setSearchQuery] = useState(urlParams?.get('q') || '')
    const [noSearchResults, setNoSearchResults] = useState(false)
    const [isFormSubmitted, setIsFormSubmitted] = useState(false)
    const [pageTitle, setPageTitle] = useState('')
    const [goBackText, setGoBackText] = useState('')
    const [goBackToPath, setGoBackToPath] = useState(routingObject.FAQ_PATH)
    const [hasSubcategory, setHasSubcategory] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const {data: contentstackData} = useGetPageByType('faq_page')
    const faqBannerData = contentstackData?.top_banner_component?.[0]
    const intl = useIntl()
    const {searchAndTitleStyles} = styles || {}
    const PageNotFound = loadable(() => import('./../page-not-found'))

    const [selectedCategory, setSelectedCategory] = useState(null)

    const {customParams} = getConfig()
    // const {locale} = useMultiSite()
    // const localeAlias = locale?.alias || ''

    const handleCategorySelect = (category) => {
        setSelectedCategory(category)
    }

    const {data: listOfCategories, isLoading: isListOfCategoriesLoading} =
        usePHPKBbyCountryAndLanguage(
            {
                name: 'WSS',
            },
            {
                enabled: true,
            },
        )

    // Extract article_id from URL for article pages (enables SSR data fetch)
    const articleIdMatch = location.pathname.match(/\/a\/(\d+)$/)
    const urlArticleId = articleIdMatch ? articleIdMatch[1] : null
    const {data: articleData} = usePHPKBGetArticle(
        {article_id: urlArticleId || ''},
        {enabled: Boolean(urlArticleId)},
    )
    const article = articleData?.hits?.hits[0]?._source || null

    // Derive pageTitle / goBackText / goBackToPath from URL + listOfCategories
    // so they are available during SSR (useEffect in children won't run server-side).
    const derived = useMemo(() => {
        const faqBase = routingObject.FAQ_PATH
        const pathAfterFaq = location.pathname.split(faqBase)[1] || ''
        const categoriesData = listOfCategories?.data

        if (!pathAfterFaq || pathAfterFaq === '/') return null // root – use defaults

        // /faq/search
        if (pathAfterFaq === '/search') {
            return {pageTitle: 'default', goBackText: 'default', goBackToPath: faqBase}
        }

        // /faq/:article_title/a/:article_id — use article data if available
        if (/\/[^/]+\/a\/\d+$/.test(pathAfterFaq) && article && categoriesData) {
            const formatName = (name) =>
                decodeHtmlEntities(name).trim().replace(/\s+/g, '-').replace(/[?]/g, '')
            const catId = article.category_id?.toString()
            const allCategories = Object.values(categoriesData)

            // Find the category path (same logic as ArticlePage.buildUlr)
            let goBackPath = faqBase
            const topLevel = allCategories.find((c) => c.category_id === catId)
            if (topLevel) {
                goBackPath = `${faqBase}/${formatName(topLevel.category_name)}/t/${catId}`
            } else {
                for (const cat of allCategories) {
                    const topic = Object.values(cat.topics || {}).find(
                        (t) => t.category_id === catId,
                    )
                    if (topic) {
                        goBackPath = `${faqBase}/${formatName(cat.category_name)}/${formatName(
                            topic.category_name,
                        )}/t/${topic.category_id}`
                        break
                    }
                }
            }
            return {
                pageTitle: article.article_title || '',
                goBackText: article.category_name || 'default',
                goBackToPath: goBackPath,
            }
        }
        if (/\/[^/]+\/a\/\d+$/.test(pathAfterFaq)) {
            return {pageTitle: '', goBackText: 'default', goBackToPath: faqBase}
        }

        // /faq/:parent_name/:cat_name/t/:cat_id  (subcategory → articles)
        const subCatMatch = pathAfterFaq.match(/\/([^/]+)\/[^/]+\/t\/(\d+)$/)
        if (subCatMatch && categoriesData) {
            const [, parentSlug, catId] = subCatMatch
            let foundCategory = null
            let parentCategory = null
            for (const cat of Object.values(categoriesData)) {
                if (cat.topics?.[catId]) {
                    foundCategory = cat.topics[catId]
                    parentCategory = cat
                    break
                }
            }
            return {
                pageTitle: foundCategory?.category_name || '',
                goBackText: parentCategory?.category_name || 'default',
                goBackToPath: parentCategory
                    ? `${faqBase}/${parentSlug}/t/${parentCategory.category_id}`
                    : faqBase,
            }
        }

        // /faq/:cat_name/t/:cat_id  (top-level category)
        const catMatch = pathAfterFaq.match(/\/[^/]+\/t\/(\d+)$/)
        if (catMatch && categoriesData) {
            const category = categoriesData[catMatch[1]]
            return {
                pageTitle: category?.category_name || '',
                goBackText: 'default',
                goBackToPath: faqBase,
            }
        }

        return null
    }, [location.pathname, listOfCategories, article])

    // Effective values: prefer state set by children's useEffect, fall back to derived
    const effectivePageTitle = pageTitle || derived?.pageTitle || ''
    const effectiveGoBackText = goBackText || derived?.goBackText || ''
    const effectiveGoBackToPath = goBackText
        ? goBackToPath
        : derived?.goBackToPath || routingObject.FAQ_PATH

    const navigate = useNavigation()
    const {
        data: queryResults,
        refetch: fetchQueryData,
        isLoading: isQuerySearchLoading,
    } = usePHPBKFreeTextSearch({q: searchQuery}, {enabled: false})

    const formatSearchSuggestions = (articleSuggestions) => {
        if (!articleSuggestions) return []
        return articleSuggestions.hits.hits.map((article) => ({
            article_id: article._source.article_id,
            article_category: article._source.category_name,
            article_title: article._source.article_title,
        }))
    }

    const formatedSuggestions = useMemo(() => {
        let articleSuggestions = formatSearchSuggestions(queryResults)
        return {articleSuggestions}
    }, [queryResults])

    const memoizedFetchCall = useCallback(() => {
        fetchQueryData()
    }, [fetchQueryData])

    useEffect(() => {
        if (!queryResults?.hits?.hits) return
        const hasResults = queryResults.hits.hits.length > 0
        if (hasResults && isFormSubmitted) {
            navigate(`${path}/search`)
        }
    }, [queryResults, navigate, path, isFormSubmitted])

    useEffect(() => {
        setNoSearchResults(false)
        if (!searchQuery || searchQuery.length < 1 || searchQuery.length < SUGGESTIONS_LENGTH)
            return
        memoizedFetchCall()
    }, [searchQuery])

    useEffect(() => {
        if (location.pathname.endsWith('/search')) {
            setIsLoading(false)
        }
    }, [location.pathname])

    const submitSearchHandler = (e, inputValue) => {
        e.preventDefault()
        let searchQuery = inputValue?.current?.value
        if (!searchQuery) return
        setSearchQuery(searchQuery)
        setIsFormSubmitted(true)
        const hasResults = queryResults.hits.hits.length > 0
        if (!hasResults) {
            setNoSearchResults(!hasResults)
        }
    }

    const debouncedSearch = debounce((input) => {
        debouncedSearch.cancel()
        setSearchQuery(input)
    }, 300)

    const handleOnInputChangeCustom = (e) => {
        setIsFormSubmitted(false)
        setNoSearchResults(false)
        let searchQuery = e?.target?.value
        if (searchQuery.length >= SUGGESTIONS_LENGTH) {
            debouncedSearch(searchQuery)
            setSearchQuery(searchQuery)
        }
    }
    const sites = getSites()
    const links = useMemo(() => {
        if (location.pathname.endsWith(routingObject.FAQ_PATH)) {
            return sites
                .map((site) =>
                    site.l10n.supportedLocales.map((locale) => {
                        return {
                            hreflang: locale.alias,
                            url: `/${locale.alias}${routingObject.FAQ_PATH}`,
                        }
                    }),
                )
                ?.flat()
        }
        return []
    })

    const breadcrumbCategories = [
        {
            id: 'home',
            name: intl.formatMessage({
                id: 'faq_page.breadcrumbs.return_to_home',
                defaultMessage: 'Return to homepage',
            }),
            c_slugUrl: '/',
        },
        {
            id: 'faq',
            name: intl.formatMessage({
                id: 'faq_page.breadcrumbs.customer_service_and_support',
                defaultMessage: 'Customer Service and Support',
            }),
            c_slugUrl: location.pathname,
        },
    ]

    const {metaTitle, metaDescription} = useFaqPageMeta()
    const currentPath = 'https://' + customParams.currentHost + location.pathname

    const renderFaqNotFound = () => {
        if (res) res.status(404)
        return <PageNotFound />
    }

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: metaTitle,
        description: metaDescription,
        url: currentPath,
    }

    const breadcrumbListSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbCategories.map((cat, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item: {
                '@id': 'https://' + customParams.currentHost + (cat.c_slugUrl || ''),
                name: cat.name,
            },
        })),
    }

    // function getItemListForCurrentPage() {
    //     if (!listOfCategories?.data) {
    //         return []
    //     }

    //     const allParentIds = Object.values(listOfCategories.data).map((cat) => cat.parent_id)
    //     if (location.pathname.endsWith(routingObject.FAQ_PATH)) {
    //         const topCategories = Object.values(listOfCategories.data)
    //             .filter(
    //                 (cat) => !allParentIds.includes(cat.category_id) && cat.category_show === 'yes',
    //             )
    //             .map((cat) => ({
    //                 name: cat.category_name,
    //                 url: `https://${customParams.currentHost}/${localeAlias}${
    //                     routingObject.FAQ_PATH
    //                 }/${decodeHtmlEntities(cat.category_name)
    //                     .trim()
    //                     .replace(/\s+/g, '-')
    //                     .replace(/[?]/g, '')}/t/${cat.category_id}`,
    //                 image: cat.category_icon,
    //             }))
    //         return topCategories
    //     }

    //     if (/\/t\/\d+$/.test(location.pathname) && selectedCategory && selectedCategory.topics) {
    //         return Object.values(selectedCategory.topics)
    //             .filter((subcat) => subcat.category_show === 'yes')
    //             .map((subcat) => ({
    //                 name: subcat.category_name,
    //                 url: `https://${customParams.currentHost}/${localeAlias}${
    //                     routingObject.FAQ_PATH
    //                 }/${decodeHtmlEntities(selectedCategory.category_name)
    //                     .trim()
    //                     .replace(/\s+/g, '-')}/${decodeHtmlEntities(subcat.category_name)
    //                     .trim()
    //                     .replace(/\s+/g, '-')}/t/${subcat.category_id}`,
    //                 image: subcat.category_icon,
    //             }))
    //     }

    //     if (/\/t\/\d+$/.test(location.pathname) && Array.isArray(queryResults?.hits?.hits)) {
    //         return queryResults.hits.hits.map((article) => ({
    //             name: article._source.article_title,
    //             url: `https://${customParams.currentHost}/${localeAlias}${location.pathname}/a/${article._source.article_id}`,
    //             image: article._source.article_image_url,
    //         }))
    //     }

    //     if (location.pathname.endsWith('/search') && Array.isArray(queryResults?.hits?.hits)) {
    //         return queryResults.hits.hits.map((article) => ({
    //             name: article._source.article_title,
    //             url: `https://${customParams.currentHost}/${localeAlias}${routingObject.FAQ_PATH}/a/${article._source.article_id}`,
    //             image: article._source.article_image_url,
    //         }))
    //     }

    //     if (/\/a\/\d+$/.test(location.pathname) && queryResults?.hits?.hits?.length === 1) {
    //         const article = queryResults.hits.hits[0]._source
    //         return [
    //             {
    //                 name: article.article_title,
    //                 url: `https://${customParams.currentHost}/${localeAlias}${location.pathname}`,
    //                 image: article.article_image_url,
    //             },
    //         ]
    //     }

    //     return []
    // }

    // const itemListElements = getItemListForCurrentPage()

    // const itemListSchemaMarkup =
    //     itemListElements.length > 0
    //         ? {
    //               '@context': 'https://schema.org',
    //               '@type': 'ItemList',
    //               itemListElement: itemListElements.map((cat, idx) => ({
    //                   '@type': 'ListItem',
    //                   position: idx + 1,
    //                   name: cat.name,
    //                   url: cat.url,
    //                   image: cat.image,
    //               })),
    //           }
    //         : null

    return (
        <VStack {...pageContainer} data-gtm="faq-page" data-cs-label="faq">
            <Helmet>
                {metaTitle && <title>{metaTitle}</title>}
                {metaDescription && <meta name="description" content={metaDescription} />}
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbListSchemaMarkup, null, 2)}
                </script>
                {/* <script type="application/ld+json">
                    {JSON.stringify(itemListSchemaMarkup, null, 2)}
                </script> */}
            </Helmet>
            {links.length > 0 && <AlternateMetaTag links={links} />}
            {isListOfCategoriesLoading || (isLoading && <LoadingSpinner />)}

            <VStack {...styles.breadAndBannerContainer}>
                <Flex {...styles.breadcrumbContainer}>
                    <Breadcrumb
                        categories={breadcrumbCategories}
                        w="100%"
                        h="100%"
                        alignSelf="flex-start"
                    />
                </Flex>
                <BannerLiveChat />
            </VStack>
            {location.pathname.endsWith(routingObject.FAQ_PATH) || effectiveGoBackText === '' ? (
                <></>
            ) : (
                <GoBackButton
                    goBackText={effectiveGoBackText}
                    goBackToPath={effectiveGoBackToPath}
                    hasSubcategory={hasSubcategory}
                />
            )}
            {faqBannerData && (faqBannerData.banner_title || faqBannerData.banner_subtitle) && (
                <FaqsBanner
                    bodycopy={faqBannerData?.banner_title}
                    heading={faqBannerData?.banner_subtitle}
                    icon={faqBannerData?.images}
                />
            )}
            <VStack w="100%" h="100%" alignItems="flex-start" gap={remCalc(0)}>
                <FaqTitle
                    title={
                        location.pathname.endsWith(routingObject.FAQ_PATH)
                            ? 'default'
                            : effectivePageTitle || 'default'
                    }
                />
                {!location.pathname.includes('/a/') && (
                    <VStack id="js-r-entry-faq-search" {...styles.searchContainerGap}>
                        <Text {...searchAndTitleStyles?.subTitleText}>
                            <FormattedMessage
                                id="faq_page.search.contact"
                                defaultMessage="Send us a question"
                            />
                        </Text>
                        <Search
                            searchInputPlaceholder={
                                <FormattedMessage defaultMessage="Search" id="global.search" />
                            }
                            onSubmitCustom={submitSearchHandler}
                            onSearchInputChangeCustom={handleOnInputChangeCustom}
                            variant="faqSearch"
                            customSuggestionData={formatedSuggestions}
                            showSpellingMessage={noSearchResults}
                        />
                    </VStack>
                )}
            </VStack>

            <Switch>
                <Route exact path={`${path}`}>
                    <CategoriesList
                        setGoBackText={setGoBackText}
                        setGoBackToPath={setGoBackToPath}
                        setHasSubcategory={setHasSubcategory}
                        onCategorySelect={handleCategorySelect}
                        listOfCategories={listOfCategories}
                    />
                </Route>
                <Route exact path={`${path}/:category_name/t/:category_id`}>
                    <CategoriesRouter
                        setGoBackText={setGoBackText}
                        setGoBackToPath={setGoBackToPath}
                        selectedCategory={selectedCategory}
                        setPageTitle={setPageTitle}
                        listOfCategories={listOfCategories}
                        setIsLoading={setIsLoading}
                    />
                </Route>
                <Route exact path={`${path}/:parent_category_name/:category_name/t/:category_id`}>
                    <ArticlesList
                        setGoBackText={setGoBackText}
                        setGoBackToPath={setGoBackToPath}
                        setPageTitle={setPageTitle}
                        listOfCategories={listOfCategories}
                        setIsLoading={setIsLoading}
                    />
                </Route>
                <Route exact path={`${path}/search`}>
                    <SearchResults
                        articles={queryResults?.hits?.hits ?? []}
                        setGoBackText={setGoBackText}
                        setGoBackToPath={setGoBackToPath}
                        setPageTitle={setPageTitle}
                        isLoading={isQuerySearchLoading}
                    />
                </Route>
                <Route exact path={`${path}/:article_title/a/:article_id`}>
                    <ArticlePage
                        setGoBackText={setGoBackText}
                        setGoBackToPath={setGoBackToPath}
                        setPageTitle={setPageTitle}
                        listOfCategories={listOfCategories}
                        setIsLoading={setIsLoading}
                    />
                </Route>
                <Route path="*" render={renderFaqNotFound} />
            </Switch>

            {contentstackData?.bottom_components?.map((component) => (
                <EditorialComponentsFactory
                    key={component.uid}
                    componentType={component._content_type_uid}
                    {...component}
                />
            ))}
        </VStack>
    )
}
